/**
 * OpenTelemetry Metrics + Core Web Vitals for the Observ RUM SDK (parity story).
 *
 * Net-new "metrics" channel — the third OTLP/HTTP signal, exported to
 * `{endpoint}/v1/metrics` on its own `MeterProvider` (the heavy `/v1/replay`
 * channel is never crossed). It captures Core Web Vitals (LCP / INP / CLS / FCP /
 * TTFB) as **histograms** (queryable as metrics) and ALSO mirrors each as an
 * `observ.session.web_vital` **log event** carrying `session.id`, so a single
 * vital appears both on the metrics dashboards and on the session timeline.
 *
 * Gated by `disableMetrics` (default ON). Like the rest of the SDK it is
 * best-effort and never throws to the host page.
 */
import { type Meter } from '@opentelemetry/api'
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http'
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics'
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals'

import { buildResource } from './resource.js'
import { emitSessionEvent, setupOtelLogs } from './otel-logs.js'
import type { ObservInitOptions } from './types.js'

/** Export interval for the periodic metric reader (ms). */
const METRIC_EXPORT_INTERVAL_MS = 30_000

/** Web-Vital name → histogram spec. CLS is unit-less; the rest are milliseconds. */
const VITAL_HISTOGRAMS: Record<string, { name: string; unit: string; description: string }> = {
  LCP: { name: 'web_vitals.lcp', unit: 'ms', description: 'Largest Contentful Paint' },
  INP: { name: 'web_vitals.inp', unit: 'ms', description: 'Interaction to Next Paint' },
  FCP: { name: 'web_vitals.fcp', unit: 'ms', description: 'First Contentful Paint' },
  TTFB: { name: 'web_vitals.ttfb', unit: 'ms', description: 'Time To First Byte' },
  CLS: { name: 'web_vitals.cls', unit: '1', description: 'Cumulative Layout Shift' },
}

/**
 * Join an endpoint base URL with the OTLP/HTTP metrics path. Mirror of
 * {@link import('./otel-rum.js').buildTracesUrl}.
 */
export function buildMetricsUrl(endpoint: string): string {
  try {
    const url = new URL(endpoint)
    url.pathname = `${url.pathname.replace(/\/+$/, '')}/v1/metrics`
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return `${endpoint.replace(/\/+$/, '')}/v1/metrics`
  }
}

let provider: MeterProvider | null = null

/** Handle returned by {@link setupOtelMetrics} for teardown. */
export interface MetricsHandle {
  meter: Meter
  stop(): Promise<void>
}

/**
 * Wire up the meter provider + Web-Vitals capture. Idempotent: a second call is a
 * no-op returning the existing handle (first init wins, mirrors `setupOtelLogs`).
 * The `x-observ-key` header is omitted when the key is empty.
 */
export function setupOtelMetrics(options: ObservInitOptions): MetricsHandle {
  if (provider) return { meter: provider.getMeter('@observtech/rum'), stop: shutdownOtelMetrics }

  const headers: Record<string, string> = {}
  if (options.key) headers['x-observ-key'] = options.key

  const reader = new PeriodicExportingMetricReader({
    exporter: new OTLPMetricExporter({ url: buildMetricsUrl(options.endpoint), headers }),
    exportIntervalMillis: METRIC_EXPORT_INTERVAL_MS,
  })

  const p = new MeterProvider({ resource: buildResource(options), readers: [reader] })
  const meter = p.getMeter('@observtech/rum')

  // Histograms for each Core Web Vital; the logger mirrors them as timeline events.
  const histograms = Object.fromEntries(
    Object.entries(VITAL_HISTOGRAMS).map(([key, h]) => [
      key,
      meter.createHistogram(h.name, { unit: h.unit, description: h.description }),
    ]),
  )
  // Reuse the (idempotent) logs provider so a vital also lands on the session
  // timeline as `observ.session.web_vital`. Never let a logs failure break metrics.
  let logger: ReturnType<typeof setupOtelLogs> | null = null
  try {
    logger = setupOtelLogs(options)
  } catch {
    /* metrics still work without the timeline mirror */
  }

  const record = (m: Metric): void => {
    try {
      const path = typeof location !== 'undefined' ? location.pathname : ''
      const dims = {
        'web_vital.rating': m.rating,
        'web_vital.navigation_type': m.navigationType,
        'url.path': path,
      }
      histograms[m.name]?.record(m.value, dims)
      if (logger) {
        emitSessionEvent(logger, 'observ.session.web_vital', {
          'web_vital.name': m.name,
          'web_vital.value': String(m.value),
          'web_vital.id': m.id,
          'web_vital.rating': m.rating,
          'web_vital.navigation_type': m.navigationType,
          'url.path': path,
        })
      }
    } catch {
      /* swallow — a single vital must never break the page */
    }
  }

  try {
    onLCP(record)
    onINP(record)
    onFCP(record)
    onTTFB(record)
    onCLS(record)
  } catch {
    /* web-vitals unavailable (e.g. non-browser) — provider still set up */
  }

  provider = p
  return { meter, stop: shutdownOtelMetrics }
}

/**
 * @internal Flush + shut the meter provider down and reset the idempotence guard.
 * Called by the SDK teardown.
 */
export async function shutdownOtelMetrics(): Promise<void> {
  const p = provider
  provider = null
  await p?.shutdown()
}

/** @internal Test-only: the active meter provider, or `null` when metrics are off. */
export function getActiveMeterProvider(): MeterProvider | null {
  return provider
}
